import { useCallback, useRef, useState } from 'react';

const useLongPress = (onLongPress, onClick, { shouldPreventDefault = true, delay = 500 } = {}) => {
	const [longPressTriggered, setLongPressTriggered] = useState(false);
	const [touchMoved, setTouchMoved] = useState(false);

	const timeout = useRef();
	const target = useRef();

	const preventLongPress = () => {
		if (timeout.current) clearTimeout(timeout.current);
	};

	const start = useCallback(
		event => {
			if (shouldPreventDefault && event.target) {
				event.target.addEventListener('touchend', preventDefault, { pasive: false });
				target.current = event.target;
			}

			timeout.current = setTimeout(() => {
				onLongPress(event);
				setLongPressTriggered(true);
			}, delay);
		},
		[onLongPress, delay, shouldPreventDefault]
	);

	const clear = useCallback(
		(event, shouldTriggeredClick = true) => {
			preventLongPress();
			setTouchMoved(false);
			if (shouldTriggeredClick && !longPressTriggered && !touchMoved) onClick();
			setLongPressTriggered(false);
			if (shouldPreventDefault && target.current) target.current.removeEventListener('touchend', preventDefault);
		},
		[shouldPreventDefault, onClick, longPressTriggered]
	);

	return {
		onMouseDown: e => start(e),
		onTouchStart: e => start(e),
		onMouseMove: () => {
			setTouchMoved(true);
			preventLongPress();
		},
		onTouchMove: e => {
			setTouchMoved(true);
			preventLongPress();
		},
		onMouseUp: e => clear(e),
		onMouseLeave: e => clear(e, false),
		onTouchEnd: e => clear(e),
	};
};

const isTouchEvent = event => {
	return 'touches' in event;
};

const preventDefault = event => {
	if (!isTouchEvent(event)) return;

	if (event.touches.length < 2 && event.preventDefault) event.preventDefault();
};

export default useLongPress;
